import type {
    LineEdit,
    LineEdits,
    LaborLineEdit,
    LaborLineEdits,
    LaborRowItem,
    MaterialLineItemOption,
} from "../types";
import type { MaterialRowItem } from "./materialLineItems";
import { resolveProductIdInOptions } from "./buildCategoryEditKeys";

type EditKeyEntry = {
    editKey: string;
    category: string;
    options: MaterialLineItemOption[];
};

function buildEditKeyIndex(
    materialsByGroup: Record<string, MaterialRowItem[]>,
): EditKeyEntry[] {
    const entries: EditKeyEntry[] = [];

    for (const rows of Object.values(materialsByGroup)) {
        for (const row of rows) {
            if (row.mode === "single") {
                entries.push({
                    editKey: row.id,
                    category: row.category,
                    options: row.availableProducts,
                });
                continue;
            }

            for (const line of row.lines) {
                entries.push({
                    editKey: line.lineId,
                    category: row.category,
                    options: line.availableProducts,
                });
            }
        }
    }

    return entries;
}

export function getProductCategoryForEditKey(
    materialsByGroup: Record<string, MaterialRowItem[]>,
    editKey: string,
): string | null {
    for (const entry of buildEditKeyIndex(materialsByGroup)) {
        if (entry.editKey === editKey) {
            return entry.category;
        }
    }

    return null;
}

export function hydrateMaterialsFromCart(
    cartItems: any[],
    materialsByGroup: Record<string, MaterialRowItem[]>,
    baseEdits: LineEdits,
): { materials: LineEdits; cartIds: Record<string, string> } {
    const materials: LineEdits = { ...baseEdits };
    const cartIds: Record<string, string> = {};
    const entries = buildEditKeyIndex(materialsByGroup);
    const usedEditKeys = new Set<string>();

    for (const item of cartItems || []) {
        const manufacturerProductId = item.ManufacturerProductId;
        const cartId = item.Id;
        if (!manufacturerProductId || !cartId) continue;

        const category = item.ProductCategory || item.CategoryName;
        if (!category) continue;

        for (const entry of entries) {
            if (entry.category !== category) continue;
            if (usedEditKeys.has(entry.editKey)) continue;

            const productId = resolveProductIdInOptions(
                manufacturerProductId,
                entry.options,
            );
            if (!productId) continue;

            materials[entry.editKey] = {
                selectedProductId: productId,
                quantity: Number(item.ItemCount) || 0,
                supplierUnitPrice:
                    item.UnitPrice === null || item.UnitPrice === undefined
                        ? null
                        : Number(item.UnitPrice),
                supplierId: item.SupplierId ?? null,
                supplierName: item.SupplierName ?? null,
            };
            cartIds[entry.editKey] = cartId;
            usedEditKeys.add(entry.editKey);
            break;
        }
    }

    return { materials, cartIds };
}

export function hydrateLaborFromCart(
    cartItems: any[],
    laborRows: LaborRowItem[],
    baseEdits: LaborLineEdits,
): { labor: LaborLineEdits; cartIds: Record<string, string> } {
    const labor: LaborLineEdits = { ...baseEdits };
    const cartIds: Record<string, string> = {};
    const editKeyByServiceId = new Map<string, string>();

    for (const row of laborRows) {
        editKeyByServiceId.set(row.laborServiceId, row.id);
    }

    for (const item of cartItems || []) {
        const laborServiceId = item.LaborServiceId;
        const cartId = item.Id;
        if (!laborServiceId || !cartId) continue;

        const editKey = editKeyByServiceId.get(laborServiceId);
        if (!editKey) continue;

        labor[editKey] = {
            laborServiceId,
            quantity: Number(item.ItemCount) || 0,
            supplierUnitPrice:
                item.UnitPrice === null || item.UnitPrice === undefined
                    ? null
                    : Number(item.UnitPrice),
            supplierId: item.SupplierId ?? null,
            supplierName: item.SupplierName ?? null,
        };
        cartIds[editKey] = cartId;
    }

    return { labor, cartIds };
}

export function normalizeUnitPrice(
    value: number | null | undefined,
): number | null {
    if (value === null || value === undefined || Number.isNaN(Number(value))) {
        return null;
    }
    return Number(value);
}

export function isMaterialEditEqual(
    current?: LineEdit,
    reference?: LineEdit,
): boolean {
    const from = reference ?? {
        selectedProductId: null,
        quantity: 0,
        supplierUnitPrice: null,
        supplierId: null,
        supplierName: null,
    };
    const to = current ?? {
        selectedProductId: null,
        quantity: 0,
        supplierUnitPrice: null,
        supplierId: null,
        supplierName: null,
    };

    return (
        from.selectedProductId === to.selectedProductId &&
        Number(from.quantity) === Number(to.quantity) &&
        normalizeUnitPrice(from.supplierUnitPrice) ===
            normalizeUnitPrice(to.supplierUnitPrice) &&
        (from.supplierId ?? null) === (to.supplierId ?? null) &&
        (from.supplierName ?? null) === (to.supplierName ?? null)
    );
}

export function hasMaterialChanges(
    current: LineEdits = {},
    reference: LineEdits = {},
): boolean {
    const keys = Array.from(
        new Set([...Object.keys(current), ...Object.keys(reference)]),
    );

    return keys.some(
        (key) => !isMaterialEditEqual(current[key], reference[key]),
    );
}

export function identifyMaterialChanges(
    current: LineEdits = {},
    reference: LineEdits = {},
) {
    const changes: Record<string, { from: LineEdit | undefined; to: LineEdit | undefined }> =
        {};
    const keys = Array.from(
        new Set([...Object.keys(current), ...Object.keys(reference)]),
    );

    for (const key of keys) {
        const from = reference[key];
        const to = current[key];
        if (!isMaterialEditEqual(to, from)) {
            changes[key] = { from, to };
        }
    }

    return changes;
}

export function buildCartItemPayload(
    edit: LineEdit,
    category: string | null,
) {
    const payload: Record<string, unknown> = {
        ManufacturerProductId: edit.selectedProductId,
        ItemCount: Number(edit.quantity) || 0,
    };

    if (category) {
        payload.ProductCategory = category;
    }

    const unitPrice = normalizeUnitPrice(edit.supplierUnitPrice);
    if (unitPrice !== null) {
        payload.UnitPrice = unitPrice;
    }

    return payload;
}

export function buildCartUpdatePayload(edit: LineEdit, cartId: string) {
    const payload: Record<string, unknown> = {
        Id: cartId,
        ItemCount: Number(edit.quantity) || 0,
    };

    const unitPrice = normalizeUnitPrice(edit.supplierUnitPrice);
    if (unitPrice !== null) {
        payload.UnitPrice = unitPrice;
    }

    return payload;
}

export function cloneMaterialEdit(edit: LineEdit): LineEdit {
    return {
        selectedProductId: edit.selectedProductId ?? null,
        quantity: Number(edit.quantity) || 0,
        supplierUnitPrice: normalizeUnitPrice(edit.supplierUnitPrice),
        supplierId: edit.supplierId ?? null,
        supplierName: edit.supplierName ?? null,
    };
}

export function isLaborEditEqual(
    current?: LaborLineEdit,
    reference?: LaborLineEdit,
): boolean {
    const from = reference ?? {
        laborServiceId: "",
        quantity: 0,
        supplierUnitPrice: null,
        supplierId: null,
        supplierName: null,
    };
    const to = current ?? {
        laborServiceId: "",
        quantity: 0,
        supplierUnitPrice: null,
        supplierId: null,
        supplierName: null,
    };

    return (
        from.laborServiceId === to.laborServiceId &&
        Number(from.quantity) === Number(to.quantity) &&
        normalizeUnitPrice(from.supplierUnitPrice) ===
            normalizeUnitPrice(to.supplierUnitPrice) &&
        (from.supplierId ?? null) === (to.supplierId ?? null) &&
        (from.supplierName ?? null) === (to.supplierName ?? null)
    );
}

export function hasLaborChanges(
    current: LaborLineEdits = {},
    reference: LaborLineEdits = {},
): boolean {
    const keys = Array.from(
        new Set([...Object.keys(current), ...Object.keys(reference)]),
    );

    return keys.some((key) => !isLaborEditEqual(current[key], reference[key]));
}

export function identifyLaborChanges(
    current: LaborLineEdits = {},
    reference: LaborLineEdits = {},
) {
    const changes: Record<
        string,
        { from: LaborLineEdit | undefined; to: LaborLineEdit | undefined }
    > = {};
    const keys = Array.from(
        new Set([...Object.keys(current), ...Object.keys(reference)]),
    );

    for (const key of keys) {
        const from = reference[key];
        const to = current[key];
        if (!isLaborEditEqual(to, from)) {
            changes[key] = { from, to };
        }
    }

    return changes;
}

export function buildLaborCartItemPayload(edit: LaborLineEdit) {
    const payload: Record<string, unknown> = {
        LaborServiceId: edit.laborServiceId,
        ItemCount: Number(edit.quantity) || 0,
    };

    const unitPrice = normalizeUnitPrice(edit.supplierUnitPrice);
    if (unitPrice !== null) {
        payload.UnitPrice = unitPrice;
    }

    if (edit.supplierId) {
        payload.SupplierId = edit.supplierId;
    }

    return payload;
}

export function buildLaborCartUpdatePayload(
    edit: LaborLineEdit,
    cartId: string,
) {
    const payload: Record<string, unknown> = {
        Id: cartId,
        ItemCount: Number(edit.quantity) || 0,
    };

    const unitPrice = normalizeUnitPrice(edit.supplierUnitPrice);
    if (unitPrice !== null) {
        payload.UnitPrice = unitPrice;
    }

    if (edit.supplierId) {
        payload.SupplierId = edit.supplierId;
    }

    return payload;
}

export function cloneLaborEdit(edit: LaborLineEdit): LaborLineEdit {
    return {
        laborServiceId: edit.laborServiceId,
        quantity: Number(edit.quantity) || 0,
        supplierUnitPrice: normalizeUnitPrice(edit.supplierUnitPrice),
        supplierId: edit.supplierId ?? null,
        supplierName: edit.supplierName ?? null,
    };
}
